import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  PAGE_DEFAULT_MAX_WIDTH,
  PAGE_CONTENT_VERTICAL_PADDING_CLASS,
  FULL_HEIGHT_OFFSET,
} from '../../styles/layout.constants';

/** A crumb is either a plain label/link crumb or a custom-node crumb — never both. */
type BreadcrumbItemType =
  | {
      /** Crumb text. Used as the link/page label and as the dropdown label when collapsed. */
      label: string;
      /** Destination for ancestor crumbs. Omit on the current (last) crumb. */
      href?: string;
      node?: never;
    }
  | {
      /**
       * Custom crumb content (e.g. a context-switch `BreadcrumbNavPopover` or an
       * inline button). It is rendered verbatim in place of the label/link — use for
       * levels the user can switch between (workspace/board) or that need a custom
       * click handler (drive folder navigation).
       */
      node: ReactNode;
      /** Plain-text fallback, shown if this crumb lands in the collapsed-ellipsis dropdown. */
      label?: string;
      href?: never;
    };

/**
 * Deep trails (workspace → board → sprint → item …) collapse their middle crumbs
 * behind an ellipsis dropdown once they exceed this many levels, so the trail stays
 * on one line at 375px. The first crumb and the last two are always shown.
 */
const MAX_VISIBLE_BREADCRUMBS = 4;

interface PageContainerProps {
  children: ReactNode;
  /**
   * Maximum width constraint for content.
   * - 'xl' (default): Standard pages, lists (1280px)
   * - '2xl': Dashboards with cards (1536px)
   * - 'lg': Forms, detail views, settings (1024px)
   * - 'full': Data tables, full-width layouts
   * - false: No constraint (inherits from parent)
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | false;
  breadcrumbs?: BreadcrumbItemType[];
  /** Fill viewport height minus header/padding. Use for chat, email, file browser layouts. */
  fullHeight?: boolean;
  className?: string;
  /**
   * Marks the page root so an E2E test can tell that this ROUTE has committed.
   * `v7_startTransition` + lazy route elements mean the URL advances before the
   * new route mounts, so `waitForURL` alone proves nothing — see
   * `tests/e2e/utils/route-commit.ts`. Set it on every branch a page can return
   * (loading and loaded), so the marker means "route mounted", not "data ready".
   */
  'data-testid'?: string;
}

const maxWidthClasses = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

/** Render a single crumb as a custom node, the current (non-clickable) page, or a link. */
function renderCrumb(item: BreadcrumbItemType, isCurrent: boolean): ReactNode {
  if (item.node) return item.node;
  const label = (
    <span className="block max-w-[8rem] truncate sm:max-w-[16rem]">{item.label}</span>
  );
  if (isCurrent || !item.href) {
    return <BreadcrumbPage>{label}</BreadcrumbPage>;
  }
  return (
    <BreadcrumbLink asChild>
      <RouterLink to={item.href}>{label}</RouterLink>
    </BreadcrumbLink>
  );
}

export function PageContainer({
  children,
  maxWidth = PAGE_DEFAULT_MAX_WIDTH,
  breadcrumbs,
  fullHeight = false,
  className,
  'data-testid': dataTestId,
}: PageContainerProps) {
  const { t } = useTranslation('common');

  // For deep trails, collapse the middle crumbs into an ellipsis dropdown so the
  // trail stays compact at narrow widths. Always keep the first crumb and the last two.
  const renderBreadcrumbs = (items: BreadcrumbItemType[]): ReactNode => {
    const lastIndex = items.length - 1;
    const collapse = items.length > MAX_VISIBLE_BREADCRUMBS;
    const collapsedMiddle = collapse ? items.slice(1, lastIndex - 1) : [];
    // Indices kept visible when collapsing: first crumb + last two.
    const visible = collapse ? [items[0], items[lastIndex - 1], items[lastIndex]] : items;

    const nodes: ReactNode[] = [];
    visible.forEach((item, vIndex) => {
      const isFirst = vIndex === 0;
      const isCurrent = item === items[lastIndex];
      if (!isFirst) {
        nodes.push(
          <BreadcrumbSeparator key={`sep-${vIndex}`}>
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </BreadcrumbSeparator>
        );
      }
      // Insert the ellipsis dropdown right after the first crumb when collapsing.
      if (collapse && vIndex === 1) {
        nodes.push(
          <BreadcrumbItem key="collapsed">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center hover:text-foreground"
                aria-label={t('app.more')}
              >
                <BreadcrumbEllipsis />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {collapsedMiddle.map((c, i) => (
                  <DropdownMenuItem key={i} asChild={!!c.href}>
                    {c.href ? (
                      <RouterLink to={c.href}>{c.label}</RouterLink>
                    ) : (
                      <span>{c.label ?? c.node}</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbItem>
        );
        nodes.push(
          <BreadcrumbSeparator key="sep-collapsed">
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </BreadcrumbSeparator>
        );
      }
      nodes.push(<BreadcrumbItem key={`crumb-${vIndex}`}>{renderCrumb(item, isCurrent)}</BreadcrumbItem>);
    });
    return nodes;
  };

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'mx-auto w-full',
        !fullHeight && maxWidth && maxWidthClasses[maxWidth],
        fullHeight && 'flex flex-col overflow-hidden',
        className
      )}
      style={fullHeight ? { height: `calc(100vh - ${FULL_HEIGHT_OFFSET}px)` } : undefined}
    >
      <div
        className={cn(
          !fullHeight && PAGE_CONTENT_VERTICAL_PADDING_CLASS,
          fullHeight && 'flex h-full flex-col'
        )}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumb className="mb-3 sm:mb-4">
            <BreadcrumbList>{renderBreadcrumbs(breadcrumbs)}</BreadcrumbList>
          </Breadcrumb>
        )}
        {children}
      </div>
    </div>
  );
}
